/**
 * `/settings/request-field-visibility` (TD-28 D-24〜D-28): 申請項目の表示制御。
 *
 * 「フィールド → 機密クラス → ルール」の3段構成 (§15 D-24)。この画面ではクラスと、
 * (クラス × 表示コンテキスト × 承認レベル) → 表示段階 のルールを設定する。
 * フィールドへのクラス割当は `/settings/request-types` のフィールド編集側で行う。
 *
 * ⚠️ 表示コンテキストはサーバー側で導出される (クライアントが自称できない)。この画面は
 * 「どの文脈のときに何段階まで見せるか」を決めるだけで、文脈の判定には関与しない。
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuthStore, hasPermission } from '@/stores/auth';
import { FIELD_DISPLAY_LEVEL_LABEL, FIELD_CONTEXT_LABEL } from '@/lib/request-field-visibility-labels';

type VisibilityClass = {
  class_id: string; code: string; label: string; description: string | null;
  default_display_level: string;
  // 使い勝手フィードバック③ (D-U21): null = 従来どおり (display_level に従う)
  default_is_editable: boolean | null;
  sort_order: number; status: string;
};
type VisibilityRule = {
  rule_id: string; class_id: string; context: string;
  approval_level: number; condition_set_id: string | null;
  display_level: string; is_editable: boolean | null; sort_order: number; label: string | null;
};

/** null/true/false の3値をトグルで扱うための番兵。Radix Select は空文字を値にできない。 */
const EDITABLE_TRISTATE = { INHERIT: '__inherit__', EDITABLE: '__editable__', LOCKED: '__locked__' } as const;
const editableToTristate = (v: boolean | null): string =>
  v === null ? EDITABLE_TRISTATE.INHERIT : v ? EDITABLE_TRISTATE.EDITABLE : EDITABLE_TRISTATE.LOCKED;
const tristateToEditable = (v: string): boolean | null =>
  v === EDITABLE_TRISTATE.EDITABLE ? true : v === EDITABLE_TRISTATE.LOCKED ? false : null;

/** 一覧表示用: サーバーの resolveEditable と同じ意味論のプレビュー
 *  (full 未満は保存値に関わらず必ず編集不可、§4-2)。 */
const effectiveEditableLabel = (displayLevel: string, raw: boolean | null): string => {
  if (displayLevel !== 'full') return '編集不可（表示段階により強制）';
  if (raw === null) return '編集可（既定）';
  return raw ? '編集可' : '編集不可';
};

// ⚠️ §8-55: 語彙は lib/request-field-visibility-labels.ts に集約する (申請画面と共有)
const LEVEL_LABEL = FIELD_DISPLAY_LEVEL_LABEL;
const CONTEXT_LABEL = FIELD_CONTEXT_LABEL;

const errorMessage = (e: unknown, fallback: string) => (e instanceof ApiClientError ? e.message : fallback);
/** Radix Select は空文字を値にできないため「条件なし」を表す番兵 (peer の NULL_VALUE と同じ手法)。 */
const NO_CONDITION = '__none__';
const inputCls = 'w-full min-h-[44px] sm:min-h-[38px] rounded-md border border-surface-border px-3 py-2 text-sm text-text-body bg-white';

export function RequestFieldVisibilitySettingsPage() {
  const qc = useQueryClient();
  const user = useAuthStore((st) => st.user);
  const canManage = hasPermission(user?.effective_permissions, 'request_field_visibility', 'manage', user?.non_inheriting_features);

  const [classModalOpen, setClassModalOpen] = useState(false);
  // ⚠️ 5並列レビュー Minor (QA/design-and-code/platform): PUT /classes/:id は実装済みなのに
  // 呼び出す画面が無く、運用開始後に一番触りたい default_display_level を変更できなかった。
  const [editingClass, setEditingClass] = useState<VisibilityClass | null>(null);
  const [ruleModalClassId, setRuleModalClassId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ kind: 'class' | 'rule'; id: string } | null>(null);

  const { data: classes = [], isLoading, isError } = useQuery<VisibilityClass[]>({
    queryKey: ['rfv-classes'],
    queryFn: () => apiFetch<VisibilityClass[]>('/request-field-visibility/classes'),
  });
  // ⚠️ 5並列レビュー Medium (design-and-code): 取得失敗を無視すると「ルール未設定 = すべて表示」と
  // 断定表示してしまう。セキュリティ設定画面で最悪の失敗方向のため isError を必ず見る。
  const { data: rules = [], isError: rulesError } = useQuery<VisibilityRule[]>({
    queryKey: ['rfv-rules'],
    queryFn: () => apiFetch<VisibilityRule[]>('/request-field-visibility/rules'),
  });
  // viewer コンテキストの条件付きルール用 (D-24: viewer のみ条件でさらに細分化できる)
  // ⚠️ GET /condition-sets のレスポンスは配列ではなく { sets: [...] } (peer: ApprovalsPage.tsx)
  const { data: conditionSetsData } = useQuery<{ sets: Array<{ set_id: string; label: string | null }> }>({
    queryKey: ['condition-sets', 'request_field_visibility'],
    queryFn: () => apiFetch<{ sets: Array<{ set_id: string; label: string | null }> }>(
      '/condition-sets?usage_point_id=request_field_visibility'),
  });
  const conditionSets = conditionSetsData?.sets ?? [];

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['rfv-classes'] });
    void qc.invalidateQueries({ queryKey: ['rfv-rules'] });
  };

  const deleteMutation = useMutation({
    mutationFn: (t: { kind: 'class' | 'rule'; id: string }) =>
      apiFetch(`/request-field-visibility/${t.kind === 'class' ? 'classes' : 'rules'}/${t.id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('削除しました'); setDeleteTarget(null); refresh(); },
    onError: (e) => { toast.error(errorMessage(e, '削除に失敗しました')); setDeleteTarget(null); },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-text-body">申請項目の表示制御</h1>
          <p className="text-sm text-text-sub mt-0.5 max-w-2xl">
            申請フォームの項目に「機密クラス」を割り当て、誰が・どの場面でその項目を見られるかを設定します。
            クラスを割り当てていない項目は誰にでも表示されます。
          </p>
        </div>
        {canManage && (
          <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setClassModalOpen(true)}>
            機密クラスを追加
          </Button>
        )}
      </div>

      {isLoading ? (
        <p className="text-text-sub">読み込み中…</p>
      ) : isError ? (
        <p className="text-sm text-danger-text" role="alert">設定の取得に失敗しました</p>
      ) : classes.length === 0 ? (
        <p className="text-sm text-text-sub">機密クラスがまだありません。追加すると、項目ごとに表示範囲を絞れるようになります。</p>
      ) : (
        <div className="space-y-4">
          {classes.map((cls) => {
            const clsRules = rules.filter((r) => r.class_id === cls.class_id);
            return (
              <section key={cls.class_id} className="border border-surface-border rounded-lg bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h2 className="text-sm font-medium text-text-body">{cls.label}</h2>
                    <p className="text-xs text-text-sub mt-0.5">
                      コード: {cls.code} ／ 既定: {LEVEL_LABEL[cls.default_display_level] ?? cls.default_display_level}
                      ／ {effectiveEditableLabel(cls.default_display_level, cls.default_is_editable)}
                    </p>
                    {cls.description && <p className="text-xs text-text-sub mt-1">{cls.description}</p>}
                  </div>
                  {canManage && (
                    <div className="flex gap-2">
                      <Button variant="secondary" size="sm" onClick={() => setRuleModalClassId(cls.class_id)}>ルール追加</Button>
                      <Button variant="secondary" size="sm" onClick={() => setEditingClass(cls)}>編集</Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget({ kind: 'class', id: cls.class_id })}>削除</Button>
                    </div>
                  )}
                </div>

                {rulesError ? (
                  <p className="text-xs text-danger-text mt-3" role="alert">
                    表示ルールの取得に失敗しました。実際に適用されている設定は不明です（再読み込みしてください）。
                  </p>
                ) : clsRules.length === 0 ? (
                  <p className="text-xs text-text-sub mt-3">
                    ルール未設定。すべての場面で「{LEVEL_LABEL[cls.default_display_level]}」が適用されます。
                  </p>
                ) : (
                  <ul className="mt-3 space-y-1">
                    {clsRules.map((r) => (
                      <li key={r.rule_id} className="flex flex-wrap items-center justify-between gap-2 text-sm border-t border-surface-border pt-2">
                        <span className="text-text-body">
                          {CONTEXT_LABEL[r.context] ?? r.context}
                          {r.context === 'approver' && (r.approval_level > 0 ? `（承認段階 ${r.approval_level}）` : '（全段階）')}
                          {r.condition_set_id && <span className="text-xs text-text-sub ml-1">条件付き</span>}
                          {' → '}
                          <span className="font-medium">{LEVEL_LABEL[r.display_level] ?? r.display_level}</span>
                          <span className="text-xs text-text-sub ml-1">（{effectiveEditableLabel(r.display_level, r.is_editable)}）</span>
                        </span>
                        {canManage && (
                          <button type="button" aria-label="このルールを削除"
                            className="min-h-[44px] min-w-[44px] sm:min-h-[36px] sm:min-w-[36px] flex items-center justify-center text-zinc-600"
                            onClick={() => setDeleteTarget({ kind: 'rule', id: r.rule_id })}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}

      {classModalOpen && <ClassModal onClose={() => setClassModalOpen(false)} onDone={() => { setClassModalOpen(false); refresh(); }} />}
      {editingClass && (
        <ClassModal initial={editingClass} onClose={() => setEditingClass(null)}
          onDone={() => { setEditingClass(null); refresh(); }} />
      )}
      {ruleModalClassId && (
        <RuleModal classId={ruleModalClassId} conditionSets={conditionSets}
          onClose={() => setRuleModalClassId(null)}
          onDone={() => { setRuleModalClassId(null); refresh(); }} />
      )}
      <ConfirmDialog open={deleteTarget !== null} title="削除しますか？"
        message={deleteTarget?.kind === 'class'
          ? 'このクラスと、紐づく表示ルールをすべて削除します。項目に割り当て済みの場合は削除できません。'
          : 'この表示ルールを削除します。'}
        confirmLabel="削除する" destructive loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
        onClose={() => setDeleteTarget(null)} />
    </div>
  );
}

function ClassModal({ initial, onClose, onDone }: {
  initial?: VisibilityClass; onClose: () => void; onDone: () => void;
}) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [defaultLevel, setDefaultLevel] = useState(initial?.default_display_level ?? 'full');
  const [defaultEditable, setDefaultEditable] = useState(editableToTristate(initial?.default_is_editable ?? null));
  const isEdit = !!initial;
  const editableDisabled = defaultLevel !== 'full';

  const mutation = useMutation({
    mutationFn: () => apiFetch(
      isEdit ? `/request-field-visibility/classes/${initial!.class_id}` : '/request-field-visibility/classes',
      {
        method: isEdit ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code, label, description: description || null, default_display_level: defaultLevel,
          default_is_editable: tristateToEditable(defaultEditable),
        }),
      },
    ),
    onSuccess: () => { toast.success(isEdit ? '機密クラスを更新しました' : '機密クラスを追加しました'); onDone(); },
    onError: (e) => toast.error(errorMessage(e, isEdit ? '更新に失敗しました' : '追加に失敗しました')),
  });

  return (
    <Modal open onClose={onClose} title={isEdit ? '機密クラスを編集' : '機密クラスを追加'}>
      <div className="space-y-4">
        <div>
          <label htmlFor="rfv-code" className="block text-sm text-text-body mb-1">コード</label>
          <input id="rfv-code" className={inputCls} value={code} onChange={(e) => setCode(e.target.value)} placeholder="sensitive" />
        </div>
        <div>
          <label htmlFor="rfv-label" className="block text-sm text-text-body mb-1">名称</label>
          <input id="rfv-label" className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="機微情報" />
        </div>
        <div>
          <label htmlFor="rfv-desc" className="block text-sm text-text-body mb-1">説明（任意）</label>
          <input id="rfv-desc" className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <label htmlFor="rfv-level" className="block text-sm text-text-body mb-1">既定の表示段階</label>
          <Select value={defaultLevel} onValueChange={setDefaultLevel}>
            <SelectTrigger id="rfv-level" className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(LEVEL_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-text-sub mt-1">
            ルールを1つも設定しない場面ではこの段階が使われます。「すべて表示」のままにすると、
            絞りたい場面だけルールを足していく形になります。
          </p>
        </div>
        <div>
          <label htmlFor="rfv-editable" className="block text-sm text-text-body mb-1">既定の編集可否</label>
          <Select value={defaultEditable} onValueChange={setDefaultEditable} disabled={editableDisabled}>
            <SelectTrigger id="rfv-editable" className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={EDITABLE_TRISTATE.INHERIT}>既定（表示段階に従う）</SelectItem>
              <SelectItem value={EDITABLE_TRISTATE.EDITABLE}>編集可</SelectItem>
              <SelectItem value={EDITABLE_TRISTATE.LOCKED}>編集不可</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-text-sub mt-1">
            {editableDisabled
              ? '表示段階が「すべて表示」以外のときは、値を編集させると見えていない値が消えるため常に編集不可になります。'
              : '「すべて表示」の場面で、値は見えても編集はさせたくない場合に「編集不可」を選びます。'}
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!code || !label} loading={mutation.isPending}
            onClick={() => mutation.mutate()}>{isEdit ? '保存' : '追加'}</Button>
        </div>
      </div>
    </Modal>
  );
}

function RuleModal({ classId, conditionSets, onClose, onDone }: {
  classId: string; conditionSets: Array<{ set_id: string; label: string | null }>;
  onClose: () => void; onDone: () => void;
}) {
  const [context, setContext] = useState('viewer');
  const [approvalLevel, setApprovalLevel] = useState('0');
  const [displayLevel, setDisplayLevel] = useState('none');
  // ⚠️ 5並列レビュー HIGH (QA/design-and-code): これが無いと condition_set を送る経路がゼロで、
  // migration 0093 が is_implemented=1 で登録した評価パスが一度も発火しない (D-186⑤/§8-122 と同型)。
  const [conditionSetId, setConditionSetId] = useState<string>(NO_CONDITION);
  const [sortOrder, setSortOrder] = useState('0');
  const [editable, setEditable] = useState<string>(EDITABLE_TRISTATE.INHERIT);
  const editableDisabled = displayLevel !== 'full';

  const mutation = useMutation({
    mutationFn: () => apiFetch('/request-field-visibility/rules', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        class_id: classId, context, display_level: displayLevel, sort_order: Number(sortOrder),
        is_editable: tristateToEditable(editable),
        ...(context === 'approver' ? { approval_level: Number(approvalLevel) } : {}),
        ...(context === 'viewer' && conditionSetId !== NO_CONDITION ? { condition_set_id: conditionSetId } : {}),
      }),
    }),
    onSuccess: () => { toast.success('表示ルールを追加しました'); onDone(); },
    onError: (e) => toast.error(errorMessage(e, '追加に失敗しました')),
  });

  return (
    <Modal open onClose={onClose} title="表示ルールを追加">
      <div className="space-y-4">
        <div>
          <label htmlFor="rfvr-ctx" className="block text-sm text-text-body mb-1">対象の場面</label>
          <Select value={context} onValueChange={setContext}>
            <SelectTrigger id="rfvr-ctx" className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(CONTEXT_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {context === 'approver' && (
          <div>
            <label htmlFor="rfvr-lv" className="block text-sm text-text-body mb-1">承認段階</label>
            <Select value={approvalLevel} onValueChange={setApprovalLevel}>
              <SelectTrigger id="rfvr-lv" className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">すべての段階</SelectItem>
                {[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{`段階 ${n}`}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-text-sub mt-1">
              段階を指定したルールが優先され、無ければ「すべての段階」のルールが使われます。
            </p>
          </div>
        )}
        {context === 'viewer' && (
          <>
            <div>
              <label htmlFor="rfvr-cs" className="block text-sm text-text-body mb-1">対象者条件 (任意)</label>
              <Select value={conditionSetId} onValueChange={setConditionSetId}>
                <SelectTrigger id="rfvr-cs" className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CONDITION}>すべての閲覧者に適用</SelectItem>
                  {conditionSets.map((cs) => <SelectItem key={cs.set_id} value={cs.set_id}>{cs.label ?? cs.set_id}</SelectItem>)}
                </SelectContent>
              </Select>
              {conditionSets.length === 0 && (
                <p className="text-xs text-text-sub mt-1">
                  条件セットがまだありません。「対象者条件」の設定画面で利用箇所「申請項目の表示制御」の条件セットを作ると、
                  閲覧者を絞ったルールを作れます。
                </p>
              )}
            </div>
            <div>
              <label htmlFor="rfvr-sort" className="block text-sm text-text-body mb-1">評価順</label>
              <input id="rfvr-sort" type="number" className={inputCls} value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)} />
              <p className="text-xs text-text-sub mt-1">
                小さいほど先に評価され、最初に条件を満たしたルールが適用されます。
              </p>
            </div>
          </>
        )}
        <div>
          <label htmlFor="rfvr-lvl" className="block text-sm text-text-body mb-1">表示段階</label>
          <Select value={displayLevel} onValueChange={setDisplayLevel}>
            <SelectTrigger id="rfvr-lvl" className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(LEVEL_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label htmlFor="rfvr-editable" className="block text-sm text-text-body mb-1">編集可否</label>
          <Select value={editable} onValueChange={setEditable} disabled={editableDisabled}>
            <SelectTrigger id="rfvr-editable" className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={EDITABLE_TRISTATE.INHERIT}>既定（クラスの設定に従う）</SelectItem>
              <SelectItem value={EDITABLE_TRISTATE.EDITABLE}>編集可</SelectItem>
              <SelectItem value={EDITABLE_TRISTATE.LOCKED}>編集不可</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-text-sub mt-1">
            {editableDisabled
              ? '表示段階が「すべて表示」以外のときは、値を編集させると見えていない値が消えるため常に編集不可になります。'
              : '「すべて表示」の場面で、値は見えても編集はさせたくない場合に「編集不可」を選びます。'}
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" loading={mutation.isPending} onClick={() => mutation.mutate()}>追加</Button>
        </div>
      </div>
    </Modal>
  );
}
